import { applicationStageMap, applicationStatusMap } from "./applicationStatusConfig";
import { installmentsTypes, schemeTypes } from "./appliedSchemesConfig";
import { officersDocumentTypeMap, statusToStageMap } from "./roleConfig";

export const landFieldGroups = {
  all: [
    "registeration_allotment_date",
    "original_lessee_department_name",
    "date_of_allotment_agreement",
    "no_of_years",
  ],
  leaseOrGovt: [
    "original_lessee_department_name",
    "date_of_allotment_agreement",
    "no_of_years",
  ],
  purchased: ["registeration_allotment_date"],
};

export const sourceFinanceCheckBoxTargets = {
  term_loan: ["term_loan_amount", "term_loan_details"],
  promoter_contribution_equity: [
    "promoter_contribution_amount",
    "promoters_contribution_details",
  ],
  unsecured_loan: ["unsecured_loan_amount", "unsecured_loan_details"],
  other_source: ["please_specify"],
};

export const sourceFinanceTableAmountTargets = {
  term_loan_details: {
    field: "total_loan_disbursed_amount",
    target: "term_loan_amount",
  },
  promoters_contribution_details: {
    field: "amount",
    target: "promoter_contribution_amount",
  },
  unsecured_loan_details: {
    field: "amount",
    target: "unsecured_loan_amount",
  },
};

export const financeCheckboxes = [
  "term_loan",
  "promoter_contribution_equity",
  "unsecured_loan",
  "other_source",
];

export const sourceofFinanceTitle = { en: "Source of Finance (Amount in INR)", hi: "वित्त का स्रोत (राशि INR में)" };

export const checkboxDetails = {
  term_loan_details: "term_loan",
  term_loan_amount: "term_loan",
  promoters_contribution_details: "promoter_contribution_equity",
  promoter_contribution_amount: "promoter_contribution_equity",
  unsecured_loan_details: "unsecured_loan",
  unsecured_loan_amount: "unsecured_loan",
  please_specify: "other_source",
};

export const LAND_TYPES = {
  OWN: "Own Land",
  PURCHASED: "Purchased Land",
  LEASE_OR_RENTAL: "Lease/Rental Land",
  GOVERNMENT_ALLOTTED: "Government Allotted",
};

export const LAND_TYPE_GROUPS = {
  all: [
    LAND_TYPES.OWN,
    LAND_TYPES.PURCHASED,
    LAND_TYPES.LEASE_OR_RENTAL,
    LAND_TYPES.GOVERNMENT_ALLOTTED,
  ],
  purchasedOnly: [LAND_TYPES.PURCHASED],
  leaseOrGovtOnly: [LAND_TYPES.LEASE_OR_RENTAL, LAND_TYPES.GOVERNMENT_ALLOTTED],
};

/**
 * Returns true if field should be hidden based on a controlling checkbox
 */
export const shouldHideCheckboxDetail = (fieldName, formData) => {
  const controllingField = checkboxDetails[fieldName];
  return controllingField ? !formData[controllingField] : false;
};

export const shouldHideLandField = (fieldName, landType) => {
  if (!landType && landFieldGroups.all.includes(fieldName)) return true;
  if (landType === LAND_TYPES.OWN && landFieldGroups.all.includes(fieldName))
    return true;
  if (
    landType === LAND_TYPES.PURCHASED &&
    landFieldGroups.purchased.includes(fieldName)
  )
    return true;
  if (
    LAND_TYPE_GROUPS.leaseOrGovtOnly.includes(landType) &&
    landFieldGroups.leaseOrGovt.includes(fieldName)
  )
    return true;
  return false;
};

export const shareholdingTables = {
  investor_details: "share_holding",
  unit_owner: "shareholding",
};

export const skipLandTypeErrors = (name) => {
  const landType = formData?.land_type;
  if (
    landFieldGroups.purchased.includes(name) &&
    landType !== LAND_TYPES.PURCHASED
  ) {
    return true;
  }
  if (
    landFieldGroups.leaseOrGovt.includes(name) &&
    ![LAND_TYPES.LEASE_OR_RENTAL, LAND_TYPES.GOVERNMENT_ALLOTTED].includes(
      landType
    )
  ) {
    return true; // skip validation
  }
  return false;
};

export const skipFinanceErrors = (fieldName, formData) => {
  // Loop over all sourceFinanceTargets
  return Object.entries(sourceFinanceCheckBoxTargets).some(
    ([checkbox, targets]) => {
      // If the field is a target
      if (targets.includes(fieldName)) {
        // Skip validation if controlling checkbox is not checked
        return !formData[checkbox];
      }
      return false;
    }
  );
};

export const mapApiDataToForm = (apiData, sections) => {
  let mappedData = {};

  sections.forEach((section) => {
    section.data.forEach((subSection) => {
      subSection.fields.forEach((field) => {
        const fieldName = field.inputProps.name;
        if (apiData[fieldName] !== undefined) {
          mappedData[fieldName] = apiData[fieldName];
        }
      });
    });
  });

  return mappedData;
};

export const isApplicationAfterApproval = (status, stage, installmentType, formData,
  incentiveClaimId) => {
  return (
    String(status) === applicationStatusMap.approved.value ||
    String(status) === applicationStatusMap.paymentDone.value ||
    String(stage) === applicationStageMap.ddo.value ||
    String(status) === applicationStatusMap.rejected.value ||
    (String(incentiveClaimId) === schemeTypes.sreimbursementcapitalincentive.value
      && installmentType === installmentsTypes.first.value && formData.slac_approved_report_capital_subsidy?.url) ||
    (String(incentiveClaimId) === schemeTypes.interestassistance.value
      && installmentType === installmentsTypes.first.value && formData.slac_approved_report_interest_assistance_first_installment?.url) ||
    (String(incentiveClaimId) === schemeTypes.interestassistance.value
      && installmentType === installmentsTypes.second.value && formData.slac_approved_report_interest_assistance_second_installment?.url) ||
    (String(incentiveClaimId) === schemeTypes.interestassistance.value
      && installmentType === installmentsTypes.third.value && formData.slac_approved_report_interest_assistance_third_installment?.url)
  );
};

const downloadTemplatewithSample = async () => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Capital Expenditure");

  // Define headers from your config
  const headers = [
    "Vendor Name *",
    "Type of Investment *",
    "Invoice Item",
    "Invoice No.",
    "Invoice Date",
    "GSTIN *",
    "Total Invoice Value (INR) *",
    "Payment Date *",
    "Mode of Payment *",
    "Payment Reference No.",
    "Amount of Payment (INR) *",
    "A/C Details From Which Payment Made",
  ];

  // Add header row
  const headerRow = sheet.addRow(headers);

  // Style header row
  headerRow.eachCell((cell) => {
    cell.font = { bold: true };
    cell.alignment = { horizontal: "center" };
    cell.protection = { locked: true }; // Lock header cells
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE5F5F4" }, // light teal background
    };
  });

  // Add some sample data rows (for testing import)
  sheet.addRow([
    "ABC Pvt Ltd",
    "Machinery",
    "Lathe Machine",
    "INV-001",
    "2025-01-10",
    "22AAAAA0000A1Z5",
    "1500000",
    "2025-01-15",
    "Bank Transfer",
    "TXN123456",
    "1500000",
    "HDFC Bank - 1234567890",
  ]);

  sheet.addRow([
    "XYZ Suppliers",
    "Raw Material",
    "Steel Rods",
    "INV-002",
    "2025-02-05",
    "27BBBBB1111B2Z6",
    "500000",
    "2025-02-10",
    "Cheque",
    "CHQ987654",
    "500000",
    "SBI Bank - 9876543210",
  ]);

  // Auto adjust column width
  sheet.columns.forEach((col, index) => {
    let maxLength = 0;
    col.eachCell({ includeEmpty: true }, (cell) => {
      const len = cell.value ? cell.value.toString().length : 10;
      if (len > maxLength) maxLength = len;
    });
    col.width = maxLength + 5; // extra padding
  });

  // Protect sheet but allow editing rows except headers
  await sheet.protect("investuk123", {
    selectLockedCells: true,
    selectUnlockedCells: true,
    deleteRows: true, // ✅ allow deleting rows
    insertRows: true, // ✅ allow inserting rows
    formatCells: true, // allow formatting of editable cells
  });

  // Unlock all rows except header
  sheet.eachRow((row, rowNumber) => {
    if (rowNumber !== 1) {
      row.eachCell((cell) => {
        cell.protection = { locked: false };
      });
    }
  });

  // Export the file
  const buf = await workbook.xlsx.writeBuffer();
  saveAs(new Blob([buf]), "Capital_Expenditure_Template.xlsx");
};

export const nicCodeToTourismProducts = {
  "41002": [
    {
      value: "Work related to maintenance of Cultural & Historical heritage only",
      label: "Work related to maintenance of Cultural & Historical heritage only",
      hi: "सांस्कृतिक और ऐतिहासिक विरासत के रखरखाव से संबंधित कार्य",
    },
  ],

  "52211": [
    {
      value: "Parking lots (in tourist locations only)",
      label: "Parking lots (in tourist locations only)",
      hi: "पार्किंग स्थल (केवल पर्यटन स्थलों पर)",
    },
  ],

  "52219": [
    {
      value: "Ropeways, funiculars etc.",
      label: "Ropeways, funiculars etc.",
      hi: "रोपवे, फ्यूनिक्युलर आदि",
    },
  ],

  "55101": [
    {
      value:
        "Expansion of existing Hotels/Resorts etc. (min. expansion/ addition of 15 rooms per Hotel/Resort unit)",
      label:
        "Expansion of existing Hotels/Resorts etc. (min. expansion/ addition of 15 rooms per Hotel/Resort unit)",
      hi:
        "मौजूदा होटलों/रिसॉर्ट्स का विस्तार (कम से कम 15 कमरे बढ़ाने/जोड़ने पर)",
    },
    {
      value: "Floatels / Floating resorts",
      label: "Floatels / Floating resorts",
      hi: "फ्लोटेल/फ्लोटिंग रिसॉर्ट",
    },
    {
      value: "Heritage Hotels / Entities",
      label: "Heritage Hotels / Entities",
      hi: "हेरिटेज होटल/संस्थान",
    },
    {
      value: "Hotels, Motels",
      label: "Hotels, Motels",
      hi: "होटल, मोटल",
    },
    {
      value: "Spa & health resorts / Wellness resorts",
      label: "Spa & health resorts / Wellness resorts",
      hi: "स्पा और हेल्थ रिसॉर्ट/वेलनेस रिसॉर्ट",
    },
    {
      value: "Tourist resort/ Tourist village",
      label: "Tourist resort/ Tourist village",
      hi: "टूरिस्ट रिसॉर्ट/टूरिस्ट विलेज",
    },
    {
      value:
        "Cruise boats, Yachts, house boats and establishment of boat clubs",
      label:
        "Cruise boats, Yachts, house boats and establishment of boat clubs",
      hi:
        "क्रूज़ बोट, यॉट्स, हाउस बोट और बोट क्लब की स्थापना",
    },
    {
      value: "Yoga, Ayurveda and Naturopathy Resorts",
      label: "Yoga, Ayurveda and Naturopathy Resorts",
      hi: "योग, आयुर्वेद और नैचुरोपैथी रिसॉर्ट",
    },
  ],

  "55200": [
    {
      value: "Ecolodges & Perennial Camps (Tents)",
      label: "Ecolodges & Perennial Camps (Tents)",
      hi: "इकोलॉज/पेरिनियल कैंप (टेंट्स)",
    },
  ],

  "56101": [
    {
      value:
        "Non-fuel wayside facilities on National or State Highways wherein restaurants & parking are provided",
      label:
        "Non-fuel wayside facilities on National or State Highways wherein restaurants & parking are provided",
      hi:
        "राष्ट्रीय या राज्य राजमार्गों पर गैर-ईंधन वे-साइड सुविधाएँ जहाँ रेस्तरां और पार्किंग उपलब्ध हो",
    },
  ],

  "82300": [
    {
      value: "MICE or convention centres",
      label: "MICE or convention centres",
      hi: "मीटिंग्स, इंसेंटिव्स, कॉन्फ्रेंस और एग्ज़ीबिशन (MICE) / कन्वेंशन सेंटर",
    },
  ],

  "85499": [
    {
      value: "Tourism or Hospitality Training Centers",
      label: "Tourism or Hospitality Training Centers",
      hi: "पर्यटन या आतिथ्य प्रशिक्षण केंद्र",
    },
  ],

  "91020": [
    {
      value: "Museums / Commercial art galleries",
      label: "Museums / Commercial art galleries",
      hi: "संग्रहालय / व्यावसायिक कला दीर्घाएँ",
    },
  ],

  "93210": [
    {
      value: "Amusement Park",
      label: "Amusement Park",
      hi: "मनोरंजन पार्क",
    },
  ],
};

export const documentTypeMap = {
  first_invoice_copy: "First Invoice Message",
  travel_trade_registration_document_cod:
    "Travel Trade Registration Document(COD)",
  letter_of_legal_authorized_person: "Letter of Legal authorized person",
  proof_of_land_ownership:
    "Proof of land ownership / land allotment certificate",
  approved_building_plan: "Approved Building Plan and permissions granted",
  valid_cto_ukpcb:
    "Valid CTO (Consent to Operation) issued by Uttarakhand Pollution Control Board (UKPCB",
  completion_occupancy_certificate: "Copy of Completion/Occupancy Certificate",
  employee_list_with_id_proof:
    "Employee List with complete address with ID proof",
  incorporation_document: "Incorporation document",
  detailed_project_report: "Detailed Project Report (DPR)",
  bank_loan_sanction_letter: "Bank Loan Sanction Letter",
  photographs_of_the_unit_service: "Photographs of the unit/ Service *",
  ca_certificate_capex: "CA certificate For CAPEX*",
  bank_transaction_proof_1: "Bank Transaction Proofs Document-1",
  bank_transaction_proof_2: "Bank Transaction Proofs Document-2",
  bank_transaction_proof_3: "Bank Transaction Proofs Document-3",
  bank_transaction_proof_4: "Bank Transaction Proofs Document-4",
  bank_transaction_proof_5: "Bank Transaction Proofs Document-5",
  chartered_engineer_certificate: "Chartered Engineer certificate of valuation",
  bank_financial_institution_loan_sanction_letter: "Bank / financial Institution’s loan sanction letter (only NBFCs and Scheduled Banks)",
  proof_payment_interest_principal_bank: "Proof of payment of interest and principal amount to bank",
  bank_certificate_annual_interest_payment: "Bank certificate stating annual interest payment and Bank Certificate for No Default & towards repayment of loan",
  pde_number_detail: "PDE Number Detail",
  bank_proof_stamp_duty: "Bank Transaction proofs of payment of stamp duty",
  loan_agreement_or_mortgaged_deed:
    "Copy of loan agreement / Copy of Mortgaged Deed clearly stating the objective of the loan being sanctioned / Mortgage",
  actual_bills_capex: "Actual Bills as per Capital Expenditure",
  capital_expenditure_details_excel:
    "Capital Expenditure Details Uploaded Excel",
  capital_expenditure_details_pdf: "Capital Expenditure Details Uploaded Pdf",
  affidavit: "Affidavit",
  gst_registration_document: "GST Registration",
  noc_fire_document: "NOC Fire Department",
  tifc_verified_report_capital_subsidy: "TIFC Verified Report - Capital Subsidy",
  tifc_unverified_report_capital_subsidy: "TIFC Unverified Report - Capital Subsidy",
  dlct_verified_report_capital_subsidy: "DLCT Verified Report - Capital Subsidy",
  dlct_unverified_report_capital_subsidy: "DLCT Unverified Report - Capital Subsidy",
  slac_approved_report_capital_subsidy: "SLAC Approved Report - Capital Subsidy",
  slac_rejection_report_capital_subsidy: "SLAC Rejection Report - Capital Subsidy",
  tifc_verified_report_interest_assistance_first_installment: "TIFC Verified Report - Interest Subsidy First Installment",
  tifc_unverified_report_interest_assistance_first_installment: "TIFC Unverified Report - Interest Subsidy First Installment",
  slac_approved_report_interest_assistance_first_installment: "SLAC Approved Report - Interest Subsidy First Installment",
  slac_rejection_report_interest_assistance_first_installment: "SLAC Rejection Report - Interest Subsidy First Installment",
  tifc_verified_report_interest_assistance_second_installment: "TIFC Verified Report - Interest Subsidy Second Installment",
  tifc_unverified_report_interest_assistance_second_installment: "TIFC Unverified Report - Interest Subsidy Second Installment",
  slac_approved_report_interest_assistance_second_installment: "SLAC Approved Report - Interest Subsidy Second Installment",
  slac_rejection_report_interest_assistance_second_installment: "SLAC Rejection Report - Interest Subsidy Second Installment",
  tifc_verified_report_interest_assistance_third_installment: "TIFC Verified Report - Interest Subsidy Third Installment",
  tifc_unverified_report_interest_assistance_third_installment: "TIFC Unverified Report - Interest Subsidy Third Installment",
  slac_approved_report_interest_assistance_third_installment: "SLAC Approved Report - Interest Subsidy Third Installment",
  slac_rejection_report_interest_assistance_third_installment: "SLAC Rejection Report - Interest Subsidy Third Installment",
  photocopy_bank_passbook_cancelled_check: "Photocopy of Bank Passbook or Cancelled Check",
  itr_return_file_second_installment: "ITR Return File",
  electricity_bill_last_six_months_second_installment: "Electricity Bill (last six Month)",
  gst_return_file_second_installment: "GST Return File",
  sanction_order_capital_subsidy_first_installment: "Sanction Order Capital Subsidy First Installment",
  sanction_order_capital_subsidy_second_installment: "Sanction Order Capital Subsidy Second Installment",
  sanction_order_interest_assistance_first_installment: "Sanction Order Interest Assistance First Installment",
  sanction_order_interest_assistance_second_installment: "Sanction Order Interest Assistance Second Installment",
  sanction_order_interest_assistance_third_installment: "Sanction Order Interest Assistance Third Installment",
  transaction_reference_capital_subsidy_first_installment: "Transaction Reference Capital Subsidy First Installment",
  transaction_reference_capital_subsidy_second_installment: "Transaction Reference Capital Subsidy Second Installment",
  transaction_reference_interest_assistance_first_installment: "Transaction Reference Interest Assistance First Installment",
  transaction_reference_interest_assistance_second_installment: "Transaction Reference Interest Assistance Second Installment",
  transaction_reference_interest_assistance_third_installment: "Transaction Reference Interest Assistance Third Installment",
};

export const documentTypes = {
  first_invoice_copy: { value: "1", name: "", label: "First Invoice Message" },
  travel_trade_registration_document_cod: {
    value: "2",
    name: "",
    label: "Travel Trade Registration Document(COD)",
  },
  letter_of_legal_authorized_person: {
    value: "3",
    name: "",
    label: "Letter of Legal authorized person",
  },
  proof_of_land_ownership: {
    value: "4",
    name: "",
    label: "Proof of land ownership / land allotment certificate",
  },
  approved_building_plan: {
    value: "5",
    name: "",
    label: "Approved Building Plan and permissions granted",
  },
  valid_cto_ukpcb: {
    value: "6",
    name: "",
    label:
      "Valid CTO (Consent to Operation) issued by Uttarakhand Pollution Control Board (UKPCB",
  },
  completion_occupancy_certificate: {
    value: "7",
    name: "",
    label: "Copy of Completion/Occupancy Certificate",
  },
  employee_list_with_id_proof: {
    value: "8",
    name: "",
    label: "Employee List with complete address with ID proof",
  },
  incorporation_document: {
    value: "9",
    name: "",
    label: "Incorporation document",
  },
  detailed_project_report: {
    value: "10",
    name: "",
    label: "Detailed Project Report (DPR)",
  },
  bank_loan_sanction_letter: {
    value: "11",
    name: "",
    label: "Bank Loan Sanction Letter",
  },
  photographs_of_the_unit_service: {
    value: "12",
    name: "",
    label: "Photographs of the unit/ Service *",
  },
  ca_certificate_capex: {
    value: "13",
    name: "",
    label: "CA certificate For CAPEX*",
  },
  bank_transaction_proof_1: {
    value: "14",
    name: "",
    label: "Bank Transaction Proofs Document-1",
  },
  bank_transaction_proof_2: {
    value: "15",
    name: "",
    label: "Bank Transaction Proofs Document-2",
  },
  bank_transaction_proof_3: {
    value: "16",
    name: "",
    label: "Bank Transaction Proofs Document-3",
  },
  bank_transaction_proof_4: {
    value: "17",
    name: "",
    label: "Bank Transaction Proofs Document-4",
  },
  bank_transaction_proof_5: {
    value: "18",
    name: "",
    label: "Bank Transaction Proofs Document-5",
  },
  chartered_engineer_certificate: {
    value: "19",
    name: "",
    label: "Chartered Engineer certificate of valuation",
  },
  pde_number_detail: { value: "20", name: "", label: "PDE Number Detail" },
  bank_proof_stamp_duty: {
    value: "21",
    name: "",
    label: "Bank Transaction proofs of payment of stamp duty",
  },
  loan_agreement_or_mortgaged_deed: {
    value: "22",
    name: "",
    label:
      "Copy of loan agreement / Copy of Mortgaged Deed clearly stating the objective of the loan being sanctioned / Mortgage",
  },
  actual_bills_capex: {
    value: "23",
    name: "",
    label: "Actual Bills as per Capital Expenditure",
  },
  capital_expenditure_details_excel: {
    value: "24",
    name: "",
    label: "Capital Expenditure Details Uploaded Excel",
  },
  capital_expenditure_details_pdf: {
    value: "25",
    name: "",
    label: "Capital Expenditure Details Uploaded Pdf",
  },
  affidavit: { value: "26", name: "", label: "Affidavit" },
  gst_registration_document: {
    value: "27",
    name: "",
    label: "GST Registration",
  },
  noc_fire_document: { value: "28", name: "", label: "NOC Fire Department" },
  photocopy_bank_passbook_cancelled_check: {
    value: "33",
    name: "",
    label: "Photocopy of Bank Passbook/ Cancelled Cheque",
  },
};

export const totalInvestmentFields = [
  "land_if_admissible",
  "building_civil_works",
  "plant_machinery",
  "furniture_fixtures",
  "utilities_infrastructure",
  "other_expenses",
];

export const totalNonEligibleFields = [
  "expenses_prior_in_principal",
  "expenses_no_bills_after_in_principal",
  "expenses_paid_cash_other",
  "cost_land_stamp_duty",
  "cost_consultancy_other_taxes",
  "site_development_valuation",
];

export const calculationFields = [
  ...totalInvestmentFields,
  ...totalNonEligibleFields,
  "total_capital_expenditure_amount",
  "area_category",
];

export const infoTexts = {
  declaration: {
    en: `I hereby declare that I am the owner/authorized representative of the aforesaid tourism unit, and I affirm that the details submitted in the claim application for Incentive/s under the Uttarakhand Paryatan Udhyami Protsahan Yojana are true and consistent with those provided to the Income Tax Authority. I confirm that no grant or subsidies has been received for the same items mentioned in the application from any government body (State or Centre) or organization. I acknowledge that if any false information or misrepresentation is found, I will be held liable under the law. Furthermore, I commit to operating the tourism unit for a minimum period as per the tourism policy, solely for the approved purpose of providing tourist facilities. If used otherwise, I accept that the grant amount, along with interest, will be recovered from me.`,
    hi: `मैं यह घोषणा करता/करती हूँ कि मैं उपरोक्त पर्यटन इकाई का मालिक/अधिकृत प्रतिनिधि हूँ और उत्तराखंड पर्यटन उद्यमी प्रोत्साहन योजना के तहत दिए गए प्रोत्साहन हेतु आवेदन में दिए गए विवरण सत्य और आयकर प्राधिकरण को दिए गए विवरणों के अनुरूप हैं। मैं पुष्टि करता/करती हूँ कि आवेदन में उल्लिखित समान वस्तुओं के लिए किसी भी सरकारी निकाय (राज्य या केंद्र) या किसी संगठन से कोई अनुदान/सब्सिडी प्राप्त नहीं हुई है। यदि किसी भी प्रकार की गलत जानकारी या छेड़छाड़ पाई जाती है, तो मैं कानून के अनुसार जिम्मेदार रहूँगा/रहूँगी। इसके अलावा, मैं पर्यटन नीति के अनुसार न्यूनतम अवधि तक पर्यटन इकाई का संचालन करने का वचन देता/देती हूँ और केवल अनुमोदित उद्देश्य के लिए ही इसे उपयोग में लाऊँगा/लाऊँगी। यदि इसका उपयोग अन्यथा किया गया, तो अनुदान राशि सहित ब्याज के साथ वसूली की जाएगी।`,
  },

  tentativeIncentiveNote: {
    en: `*This is the tentative incentive amount. The final incentive amount will be disbursed post department approval of your application as per the fulfillment of scheme guidelines.`,
    hi: `*यह अनुमानित प्रोत्साहन राशि है। योजना के दिशा-निर्देशों के अनुसार आपके आवेदन के विभागीय अनुमोदन के बाद अंतिम प्रोत्साहन राशि दी जाएगी।`,
  },
};

//later from API
export const maxEligibleSubsidy = 50000000;

export const amountFieldsToConvertToWords = [
  "land_if_admissible",
  "building_civil_works",
  "plant_machinery",
  "furniture_fixtures",
  "utilities_infrastructure",
  "other_expenses",
  "total_investment",
  "expenses_prior_in_principal",
  "expenses_no_bills_after_in_principal",
  "expenses_paid_cash_other",
  "cost_land_stamp_duty",
  "cost_consultancy_other_taxes",
  "site_development_valuation",
  "total_non_eligible",
  "total_eligible_investment_after_non_eligible_deduction",
  "capital_subsidy_min_investment",
  "capital_subsidy_additional_investment",
  "total_max_capital_subsidy",
  "total_interest_paid_first_installment",
  "maximum_eligible_amount_first_installment",
  "total_interest_paid_second_installment",
  "maximum_eligible_amount_second_installment",
  "total_interest_paid_third_installment",
  "maximum_eligible_amount_third_installment",
];

export const amountFieldLabelsToConvertToWords = [
  "Land (if admissible)",
  "Building/Civil Works",
  "Plant & Machinery",
  "Furniture & Fixtures",
  "Utilities/Infrastructure",
  "Other Expenses",
  "Total Investment",
  "Expenses Prior to Principal Approval",
  "Expenses without Bills/After Principal",
  "Cash Payments/Other Expenses",
  "Cost of Land/Stamp Duty",
  "Consultancy/Other Taxes",
  "Site Development/Valuation",
  "Total Non-Eligible Amount",
  "Total Eligible Investment (Post Deduction)",
  "Minimum Capital Subsidy",
  "Additional Capital Subsidy",
  "Maximum Capital Subsidy",
  "Total Interest Paid in First Installment",
  "Maximum Eligible Amount (First Installment)",
  "Total Interest Paid in Second Installment",
  "Maximum Eligible Amount (Second Installment)",
  "Total Interest Paid in Third Installment",
  "Maximum Eligible Amount (Third Installment)",
  "Total Eligible Investment",
  "Capital Subsidy For Min Investment (1 Cr.)",
  "Capital Subsidy For Additional Investment (<1 & >5Cr.)",
  "Total Maximum Capital Subsidy",
  "Total Stamp Duty Paid Per Year (INR)",
  "Total Stamp Duty per Year",
  "Recommended Eligible Investment",
  "Recommended Stamp Duty",
  "Maximum Eligible Amount",
  "Recommended Capital Subsidy Second Installment",
  "Recommended Interest Subsidy First Installment",
  "Recommended Interest Subsidy Second Installment",
  "Recommended Interest Subsidy Third Installment",
  "Approved Eligible Investment",
  "Approved Maximum Capital Subsidy",
  "Disbursed Capital Subsidy - First Installment",
  "Remaining Capital Subsidy Second Installment",
];

export const amountFieldLabelsToConvertInCrores = [
  "Investment in Land",
  "Investment in Building",
  "Investment in Plant & Equipment",
  "Investment in Other",
  "Total Investment Amount"
];


export const allNicOptions = [
  { label: "52211", value: "52211", hi: "52211" },
  { label: "52219", value: "52219", hi: "52219" },
  { label: "55101", value: "55101", hi: "55101" },
  { label: "82300", value: "82300", hi: "82300" },
  { label: "55200", value: "55200", hi: "55200" },
  { label: "56101", value: "56101", hi: "56101" },
  { label: "85499", value: "85499", hi: "85499" },
  { label: "91020", value: "91020", hi: "91020" },
  { label: "93210", value: "93210", hi: "93210" },
  { label: "41002", value: "41002", hi: "41002" },
];

export const insertAssistanceDocuments = [
  "bank_financial_institution_loan_sanction_letter",
  "proof_payment_interest_principal_bank",
  "bank_certificate_annual_interest_payment",
];

export const capitalSubsidySecondInstallmentDocuments = [
  "itr_return_file_second_installment",
  "electricity_bill_last_six_months_second_installment",
  "gst_return_file_second_installment",
]

export const insterstSubsidyBankDetailsFieldsFirstInstallment = [
  "loan_interest_details_interest_subsidy_first_installment",
  "total_interest_paid_first_installment",
  "maximum_eligible_amount_first_installment"
];

export const insterstSubsidyBankDetailsFieldsSecondInstallment = [
  "loan_interest_details_interest_subsidy_second_installment",
  "total_interest_paid_second_installment",
  "maximum_eligible_amount_second_installment"
];

export const insterstSubsidyBankDetailsFieldsThirdInstallment = [
  "loan_interest_details_interest_subsidy_third_installment",
  "total_interest_paid_third_installment",
  "maximum_eligible_amount_third_installment"
];

export const calculateEmploymentFields = (data) => {
  const male_bonafied = parseFloat(data.male_bonafied || 0);
  const female_bonafied = parseFloat(data.female_bonafied || 0);
  const others_bonafied = parseFloat(data.others_bonafied || 0);
  const male_non_bonafied = parseFloat(data.male_non_bonafied || 0);
  const female_non_bonafied = parseFloat(data.female_non_bonafied || 0);
  const others_non_bonafied = parseFloat(data.others_non_bonafied || 0);

  const male_total = male_bonafied + male_non_bonafied;
  const female_total = female_bonafied + female_non_bonafied;
  const others_total = others_bonafied + others_non_bonafied;
  const total_permanent_employees = male_total + female_total + others_total;

  const totalBonafied = male_bonafied + female_bonafied + others_bonafied;

  const bonafied_employees_percentage =
    total_permanent_employees > 0
      ? Number(((totalBonafied / total_permanent_employees) * 100).toFixed(2))
      : 0;

  return {
    male_total,
    female_total,
    others_total,
    total_permanent_employees,
    bonafied_employees_percentage,
  };
};

export const employmentFields = [
  "male_bonafied",
  "female_bonafied",
  "others_bonafied",
  "male_non_bonafied",
  "female_non_bonafied",
  "others_non_bonafied",
];

export const loanTables = [
  "term_loan_details",
  "promoters_contribution_details",
  "unsecured_loan_details",
  "bank_details_used_for_project",
  "authorized_person"
]

export const calculateInvestmentAndSubsidy = (formData) => {
  const updated = { ...formData };

  // ---------------- Determine Total Eligible Investment ----------------
  // Use recommended_investment if entered, else compute normally
  const recommendedInvestment = parseFloat(updated.recommended_investment);
  let totalEligibleInvestment =
    !isNaN(recommendedInvestment) && recommendedInvestment > 0
      ? recommendedInvestment
      : (parseFloat(updated.total_capital_expenditure_amount) || 0) +
      totalInvestmentFields.reduce(
        (sum, field) => sum + (parseFloat(updated[field]) || 0),
        0
      ) -
      totalNonEligibleFields.reduce(
        (sum, field) => sum + (parseFloat(updated[field]) || 0),
        0
      );

  //updated.total_eligible_investment_after_non_eligible_deduction =
  //totalEligibleInvestment.toFixed(2);

  // ---------------- Validation & Capital Subsidy ----------------
  const errors = {};

  const minInvestment = 10000000; // 1 Cr
  const minSubsidy = 3300000; // 33 Lakhs
  const maxCapMap = { A: 8000000, B: 12000000, C: 15000000 };

  if (totalEligibleInvestment < minInvestment) {
    updated.capital_subsidy_min_investment = "0.00";
    updated.capital_subsidy_additional_investment = "0.00";
    updated.total_max_capital_subsidy = "0.00";

    errors.total_eligible_investment_after_non_eligible_deduction =
      "Total eligible investment must be at least 1 Crore.";
  } else if (!updated.area_category) {
    errors.capital_subsidy_additional_investment =
      "Please select Area Category first(A/B/C).";
  } else {
    updated.capital_subsidy_min_investment = minSubsidy.toFixed(2);

    let additionalInvestmentSubsidy = 0;
    if (totalEligibleInvestment > minInvestment) {
      const excessAmount = totalEligibleInvestment - minInvestment;
      if (updated.area_category === "A")
        additionalInvestmentSubsidy = excessAmount * 0.15;
      if (updated.area_category === "B")
        additionalInvestmentSubsidy = excessAmount * 0.25;
      if (updated.area_category === "C")
        additionalInvestmentSubsidy = excessAmount * 0.3;
    }

    const categoryMax = maxCapMap[updated.area_category] || 0;
    const maxAdditional = categoryMax - minSubsidy;

    if (additionalInvestmentSubsidy > maxAdditional) {
      additionalInvestmentSubsidy = maxAdditional;
    }

    updated.capital_subsidy_additional_investment =
      additionalInvestmentSubsidy.toFixed(2);
    updated.total_max_capital_subsidy = (
      minSubsidy + additionalInvestmentSubsidy
    ).toFixed(2);

    // Clear errors
    errors.capital_subsidy_additional_investment = null;
    errors.total_max_capital_subsidy = null;
    errors.total_eligible_investment_after_non_eligible_deduction = null;
  }

  return { updated, errors };
};

export const getOffciersUploadedDocumentName = ({
  status,
  incentiveClaimId,
  installmentNo,
}) => {
  const stage = statusToStageMap[status];
  if (!stage) return null;

  // CAPITAL SUBSIDY
  if (incentiveClaimId === "1") {
    return officersDocumentTypeMap.capital_subsidy[stage] ?? null;
  }

  // INTEREST ASSISTANCE
  if (incentiveClaimId === "2" && installmentNo) {
    return (
      officersDocumentTypeMap.interest_assistance?.[installmentNo]?.[stage] ?? null
    );
  }

  return null;
};